import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { InlineCta } from "@/components/site/InlineCta";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";
import { GuideLinks } from "../../components/site/Guides";
import { KnowledgeHubNote } from "../../components/site/KnowledgeHubNote";
import { NewcomerNote } from "../../components/site/NewcomerNote";

const title = "Distance learning at secondary school and sixth form abroad";
const description =
  "How Swedish distance learning via Sofia Distans and Hermods works with on-site supervision at our school in Estepona – structure, support and daily routines.";

export const Route = createFileRoute("/en/distance-learning-abroad")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/distance-learning-abroad") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/distance-learning-abroad"),
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        eyebrow="Guide"
        title="Distance learning at secondary school and sixth form"
        intro="From Year 7, our pupils study via Swedish distance education, but do so together in a school environment with supervision. Here's how the set-up works and who it suits."
      />

      <NewcomerNote />

      <Prose>
        <h2>Two parts that fit together</h2>
        <p>
          Distance learning abroad consists of two things. One is the education itself: course
          content, assignments, tests, assessment and grades come from a Swedish distance school –
          Sofia Distans for lower secondary and Hermods for upper secondary. The other is everyday
          life: where the pupil sits, how the day is structured, what support is available and who
          they spend time with. We're responsible for that part.
        </p>
        <p>
          The combination is the whole point. Distance education done alone at the kitchen table
          places very high demands on self-discipline and can easily become isolating too. With a
          fixed school day, supervision and other pupils in the same situation, the format becomes
          far more sustainable.
        </p>

        <h2>A typical school day</h2>
        <p>
          The pupil comes to school in the morning and works through their courses according to a
          plan. The supervisor follows up on progress, helps structure the week, provides support
          when something is difficult, and stays in touch with the distance school when needed. The
          day has start and finish times, breaks and shared activities – it looks much more like
          school than homework.
        </p>
        <p>
          A big advantage is that pace can be adapted. A pupil who's ahead in one subject can move
          on without waiting for a class, while a pupil who needs more time gets it without falling
          behind in everything else.
        </p>

        <h2>Grades and eligibility</h2>
        <p>
          Since the courses are studied within the Swedish system, grades are issued by the
          distance school according to Swedish syllabuses. That means the pupil builds on the same
          foundation as classmates in Sweden and can apply onward to Swedish upper secondary school
          or further studies on the same terms. For families planning to move back, that's often the
          decisive advantage over a local system.
        </p>
      </Prose>

      <div className="mx-auto max-w-3xl px-5">
        <InlineCta locale="en" />
      </div>

      <Prose>

        <h2>Combining studies with sport</h2>
        <p>
          The distance format makes the timetable flexible, which is what makes it possible to
          combine school with serious training. Pupils in{" "}
          <Link className="text-primary link-quiet" to="/en/football-academy">
            Malmö FF's football academy
          </Link>{" "}
          and the{" "}
          <Link className="text-primary link-quiet" to="/en/padel-tennis">
            padel and tennis pathway
          </Link>{" "}
          build study time around training sessions rather than the other way round. The climate
          allows training outdoors year-round, giving more quality sessions than a normal Swedish
          season.
        </p>

        <h2>Who does it suit?</h2>
        <p>
          The set-up works best for pupils who can manage independent work during the day and who
          accept support when they get stuck. Motivation matters more than grades – a pupil who
          wants to but needs structure usually does well, while a pupil who'd rather avoid
          schoolwork finds it tougher in a format built on personal responsibility.
        </p>
        <p>
          We're upfront about this in conversations before a start. It's better to talk through the
          conditions beforehand than to discover halfway through a term that the format doesn't
          suit. Where extra support is needed, we look together at what's reasonable to offer; read
          more about our work with{" "}
          <Link className="text-primary link-quiet" to="/en/extra-support">
            pupils needing extra support
          </Link>
          .
        </p>

        <h2>What parents often ask</h2>
        <ul>
          <li>
            <strong>Is the pupil isolated?</strong> No – studies take place in a group at school,
            with other pupils and adults around them.
          </li>
          <li>
            <strong>How do we follow our child's progress as parents?</strong> Through the distance
            school's system and through ongoing contact with the supervisor.
          </li>
          <li>
            <strong>Is it possible to start mid-term?</strong> Yes, applications are accepted on a
            rolling basis.
          </li>
          <li>
            <strong>What does it cost?</strong> See{" "}
            <Link className="text-primary link-quiet" to="/en/fees">
              fees
            </Link>{" "}
            for current levels.
          </li>
        </ul>

        <h2>Next step</h2>
        <p>
          Read more about the set-up on our{" "}
          <Link
            className="text-primary link-quiet"
            to="/en/secondary-school-sixth-form"
          >
            secondary school and sixth form
          </Link>{" "}
          page, or submit an application directly. We'll get back to you with news on availability
          and go through what applies for your child.
        </p>
      </Prose>

      <KnowledgeHubNote resource="flyttchecklistan" locale="en" />

      <GuideLinks current="/distansstudier-hogstadiet-gymnasiet-utomlands" locale="en" />
      <CtaBand
        title="Ready to take the next step?"
        text="Applications are accepted on a rolling basis, and it's fine to start mid-term."
      />
      <div className="h-20" />
    </>
  );
}
